import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { t } from 'i18next';
import PanelWithClassification from '../../components/PanelWithClassification';
import MainContainer from '../../components/containers/MainContainer';
import actionsFactory from '../../store/actions/factory/dbItemsActionFactory';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { SearchContext } from '../../features/project/building/Building';
import NLeftPanel from '../../components/NLeftPanel';
import { useMutation, useQueryClient } from 'react-query';
import { apiService } from '../api';
import { emptyValuesAsUndefined } from '../form';
import { getApiErrorMessage, getFieldErrors } from '../apiError';
import { toast } from 'sonner';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { MY_APPROVALS_KEY } from '@/features/approval/hooks/useApprovals';

export interface DbItemsWrapperOptions {
  apiName?: string;
  extraParams?: Record<string, unknown>;
}

const withDbItemsWrapper = (
  Component,
  name,
  url,
  basePath = null,
  { apiName, extraParams }: DbItemsWrapperOptions = {}
) =>
  // eslint-disable-next-line react/display-name
  function () {
    const permission = useSelector((state: any) => state.auth.pagePermission);
    const { activeClassification, detail, isLoading } = useSelector((state: any) => state[name]);

    const [openDialog, setOpenDialog] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [search, setSearch] = useState(null);
    const [filter, setFilter] = useState({});
    const splittedName = name.split(/(?=[A-Z])/);
    let queryKey = splittedName.join('-').toLowerCase();
    if (name === 'propertySet') {
      queryKey = 'set-property-definition';
    }
    if (name === 'technicalReportItemSet') {
      queryKey = 'set-technical-report-item';
    }
    if (name === 'contractListing') {
      queryKey = 'contract';
    }
    if (name === 'typeMarkTemplate') {
      queryKey = 'instance-type-template';
    }

    const apiPath = apiName ?? queryKey;
    const TYPE = splittedName.join('_').toUpperCase();

    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { fetchDetail, setActiveClassification, clear } = actionsFactory(name, true, apiName);
    const { contract, modelId } = useSelector((state: any) => state.contract);
    const contractId = contract?.id ?? null;
    const history = useHistory();
    const { id: urlId } = useParams<{ id?: string }>();
    const { search: locationSearch } = useLocation();
    const linkedId = (basePath && urlId) || new URLSearchParams(locationSearch).get('id');

    useEffect(() => {
      if (linkedId) {
        dispatch(fetchDetail(linkedId));
      }
    }, [linkedId]);

    const create = useMutation({
      mutationFn: (values: any) => {
        return apiService.post(`/api/v1/${apiPath}/`, {
          ...(values as any),
          contract: contractId,
          model: modelId,
          classification: activeClassification,
        });
      },
      onSuccess: ({ data }) => {
        queryClient.invalidateQueries(queryKey);
        // Creating a company version opens a DRAFT approval request on the backend —
        // refresh /approvals/my/ so ApprovalItemActions shows the submit button.
        queryClient.invalidateQueries([MY_APPROVALS_KEY]);
        toast.success('Položka úspěšně vytvořena');
        dispatch(fetchDetail(data.id));
        setIsEdit(false);
        setOpenDialog(false);
      },
      onError: (err) => {
        console.error(err);
        toast.error(getApiErrorMessage(err));
      },
    });

    const edit = useMutation({
      mutationFn: ({ id, values }: any) => {
        return apiService.post(`/api/v1/${apiPath}/${id}/create-version/`, {
          ...emptyValuesAsUndefined(values),
        });
      },
      onSuccess: (_, { id }: any) => {
        queryClient.invalidateQueries(queryKey);
        queryClient.invalidateQueries([MY_APPROVALS_KEY]);
        toast.success('Položka úspěšně editována');
        dispatch(fetchDetail(id));
        setIsEdit(false);
        setOpenDialog(false);
      },
      onError: (err) => {
        console.error(err);
        toast.error(getApiErrorMessage(err));
      },
    });

    const makeActive = (id) => {
      dispatch({ type: `GET_${TYPE}_DETAIL`, name: TYPE });
      dispatch(fetchDetail(id));
      if (basePath) {
        history.push(`${basePath}/${id}`);
      }
    };

    useEffect(
      () => () => {
        dispatch(clear());
      },
      []
    );

    const makeActiveClassification = (id) => {
      dispatch(setActiveClassification(id));
      queryClient.invalidateQueries(queryKey);
    };

    const handleSubmit = (id, values, helpers?: any) => {
      const onError = (err) => {
        console.error(err);
        helpers?.setErrors?.(getFieldErrors(err));
      };
      if (id) {
        (edit as any).mutate({ id, values }, { onError });
      } else {
        create.mutate(values, { onError });
      }
    };

    const handleEdit = () => {
      setIsEdit(true);
      setOpenDialog(true);
    };

    const handleClose = () => {
      setIsEdit(false);
      setOpenDialog(false);
    };

    const onSearch = async (e) => {
      setSearch(e?.target?.value);
    };

    const onFilter = async (value) => {
      setFilter(value);
    };

    return (
      <SearchContext.Provider
        value={{
          search,
          filter,
          setSearch,
          setFilter,
        }}
      >
        <MainContainer>
          <PanelWithClassification
            addItem={() => {}}
            onSearch={() => {
              dispatch(clear());
            }}
            onFilter={() => {}}
            url={url}
            filteredValues={{
              is_active: [
                { value: true as any, title: 'Aktivní' },
                { value: false as any, title: 'Neaktivní' },
              ],
            }}
            title="Třídník"
            disableAdd
            makeActive={makeActiveClassification}
            active={activeClassification}
          />
          {activeClassification && (
            <div className="h-full border-r border-neutral-150 shrink-0">
              <NLeftPanel
                queryKey={queryKey}
                title={t(name)}
                addItem={() => setOpenDialog(true)}
                submit={() => {}}
                onSearch={onSearch}
                onFilter={onFilter}
                permission={permission}
                filteredValues={{}}
                makeActive={makeActive}
                active={detail?.id}
                classification={activeClassification}
                navigationOptions={{ apiName: apiPath, extraParams }}
              />
            </div>
          )}
          {isLoading ? (
            <div className="flex w-full h-[400px] justify-center items-center">
              <SpinnerOverlay />
            </div>
          ) : (
            <Component
              detail={detail}
              openDialog={openDialog}
              isEdit={isEdit}
              handleSubmit={handleSubmit}
              handleEdit={handleEdit}
              handleClose={handleClose}
            />
          )}
        </MainContainer>
      </SearchContext.Provider>
    );
  };
export default withDbItemsWrapper;
